import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'framer-motion';

import useAnimate, { entry } from './primitives/Motion';
import GroupBadge from '../media/images/smart-solutions-group.png';
import { companies, contact } from '../data/site';

export default function Nav() {
    const [lifted, setLifted] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const { scrollY } = useScroll();
    const animate = useAnimate();

    useMotionValueEvent(scrollY, 'change', value => setLifted(value > 24));

    useEffect(() => {
        document.body.classList.toggle('is-locked', menuOpen);
        return () => document.body.classList.remove('is-locked');
    }, [menuOpen]);

    useEffect(() => {
        const onKey = event => event.key === 'Escape' && setMenuOpen(false);
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const links = [
        ...companies.map(c => ({ label: c.short, href: `#${c.id}` })),
        { label: 'Why the group', href: '#advantages' }
    ];

    return (
        <>
            <motion.header
                className={`nav${lifted ? ' nav--lifted' : ''}`}
                {...entry(animate, { y: -80, opacity: 0 })}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            >
                <div className="nav__inner">
                    <a className="nav__brand" href="#top" aria-label="The Smart Solutions Group, home">
                        <img src={GroupBadge} alt="" width="36" height="36" />
                        <span className="nav__brand-text">
                            <span className="nav__brand-lead">The Smart</span>
                            <span className="nav__brand-main">Solutions Group</span>
                        </span>
                    </a>

                    <nav className="nav__links" aria-label="Sections">
                        {links.map(link => (
                            <a key={link.href} className="nav__link" href={link.href}>
                                {link.label}
                            </a>
                        ))}
                    </nav>

                    <div className="nav__actions">
                        <a className="nav__phone" href={contact.phoneHref}>{contact.phone}</a>
                        <a className="btn btn--solid nav__cta" href="#contact">Get in touch</a>
                        <button
                            className={`nav__burger${menuOpen ? ' is-open' : ''}`}
                            onClick={() => setMenuOpen(open => !open)}
                            aria-expanded={menuOpen}
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        >
                            <span /><span />
                        </button>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        className="nav-sheet"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <nav className="nav-sheet__list" aria-label="Menu">
                            {companies.map((company, i) => (
                                <motion.a
                                    key={company.id}
                                    href={`#${company.id}`}
                                    className="nav-sheet__item"
                                    style={{ '--accent': company.accent }}
                                    onClick={() => setMenuOpen(false)}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.06 * i, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                                >
                                    <span className="nav-sheet__dot" />
                                    <span>
                                        <em>{company.short}</em>
                                        <small>{company.discipline}</small>
                                    </span>
                                </motion.a>
                            ))}

                            <motion.a
                                href="#advantages"
                                className="nav-sheet__item nav-sheet__item--plain"
                                onClick={() => setMenuOpen(false)}
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.24, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                            >
                                <span><em>Why the group</em></span>
                            </motion.a>

                            <motion.div
                                className="nav-sheet__foot"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.32, duration: 0.6 }}
                            >
                                <a className="btn btn--solid" href={contact.phoneHref}>
                                    Call {contact.phone}
                                </a>
                                <a
                                    className="btn btn--ghost"
                                    href="#contact"
                                    onClick={() => setMenuOpen(false)}
                                >
                                    Get in touch
                                </a>
                            </motion.div>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
